import React, { useState } from 'react';
import { Share2, Copy, Check, DollarSign, Eye, TrendingUp, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShareContentModal = ({ isOpen, onClose, content }) => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [referralLink, setReferralLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkGenerated, setLinkGenerated] = useState(false);

  const generateReferralLink = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/referrals/content/${content.id}/generate-link`,
        {},
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );

      setReferralLink(response.data.referral_link);
      setLinkGenerated(true);
      
      toast({
        title: 'Link Generated!',
        description: 'Your referral link is ready to share',
      });
    } catch (error) {
      console.error('Error generating referral link:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to generate referral link',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast({
      title: 'Copied!',
      description: 'Referral link copied to clipboard',
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    setLinkGenerated(false);
    setReferralLink('');
    setCopied(false);
    onClose();
  };

  if (!content) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-gray-900 border-2 border-yellow-500/50 text-white max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center">
                <Share2 className="w-6 h-6 text-black" />
              </div>
              <div>
                <DialogTitle className="text-xl">Share & Earn</DialogTitle>
                <DialogDescription className="text-gray-400">
                  Share this content and earn money
                </DialogDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </DialogHeader>

        {/* Content Info */}
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold text-white mb-1">{content.title}</h3>
            {content.description && (
              <p className="text-sm text-gray-400 line-clamp-2">{content.description}</p>
            )}
          </CardContent>
        </Card>

        {/* Earnings Info */}
        <div className="bg-gradient-to-r from-yellow-400/10 to-purple-500/10 rounded-lg p-4 border border-yellow-500/30">
          <h4 className="text-md font-semibold text-white mb-3">How You Earn:</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-medium">$0.10 per view</p>
                <p className="text-xs text-gray-400">You get 30% ($0.03), Creator gets 70% ($0.07)</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <TrendingUp className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />
              <div>
                <p className="text-white font-medium">$2.00 per subscription</p>
                <p className="text-xs text-gray-400">You get 40% ($0.80), Creator gets 60% ($1.20)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Generate Link or Show Link */}
        {!linkGenerated ? (
          <div className="text-center py-6">
            <Button
              onClick={generateReferralLink}
              disabled={loading}
              className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold px-8"
            >
              {loading ? (
                'Generating...'
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" />
                  Generate My Referral Link
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Your Referral Link</label>
              <div className="flex items-center gap-3">
                <Input
                  value={referralLink}
                  readOnly
                  className="bg-gray-900 border-gray-700 text-white flex-1"
                />
                <Button
                  onClick={handleCopy}
                  className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-bold"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Share this link on social media, messaging apps, or anywhere to start earning!
              </p>
            </div>

            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
              <p className="text-sm text-gray-300 text-center">
                💡 <strong className="text-yellow-400">Pro Tip:</strong> The more you share, the more you earn. Both you and the creator benefit when people engage with this content!
              </p>
            </div>
          </div>
        )}

        {/* Share Options Info */}
        <div className="text-center">
          <p className="text-xs text-gray-400">
            Works even if you&apos;re not logged in. Anyone can share and earn!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareContentModal;
